"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlobalInstallerResolver = void 0;
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
const os = require("os");
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const FileUtilities_1 = require("../Utils/FileUtilities");
const InstallIdUtilities_1 = require("../Utils/InstallIdUtilities");
const WebRequestWorkerSingleton_1 = require("../Utils/WebRequestWorkerSingleton");
const VersionResolver_1 = require("./VersionResolver");
const versionUtils = require("./VersionUtilities");
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * @remarks
 * This is similar to the version resolver but accepts a wider range of inputs such as '6', '6.1', or '6.0.3xx' or '6.0.301'.
 * It currently only is used for SDK Global acquisition to prevent breaking existing behaviors.
 * Throws various errors in the event that a version is incorrectly formatted, the sdk server is unavailable, etc.
 */
class GlobalInstallerResolver {
    constructor(context, requestedVersion) {
        this.context = context;
        this.releasesJsonErrorString = `The API hosting the dotnet releases.json is invalid or has changed and the extension needs to be updated. Invalid API URL: `;
        this.badResolvedVersionErrorString = `The requested version was not in the correct format. Allowable formats are
        * <MAJOR> (for example '6')
        * <MAJOR>.<minor> (for example '6.0')
        * <MAJOR>.<minor>.<feature band without patch version> (for example '6.0.4xx')
        * <MAJOR>.<minor>.<patch> (for example '6.0.402')
        * Your version was resolved to: `;
        this.releasesJsonKey = 'releases';
        this.releasesSdksKey = 'sdks';
        this.releasesSdkRidKey = 'rid';
        this.releasesSdkFileKey = 'files';
        this.releasesSdkVersionKey = 'version';
        this.releasesSdkNameKey = 'name';
        this.releasesUrlKey = 'url';
        this.releasesHashKey = 'hash';
        this.releasesLatestSdkKey = 'latest-sdk';
        /**
         * @remarks Do NOT set this unless you are testing.
         * Written to allow mock data to be given to the resolver.
         */
        this.customWebRequestWorker = null;
        this.requestedVersion = requestedVersion;
        this.discoveredInstallerUrl = '';
        this.fullySpecifiedVersionRequested = '';
        this.expectedInstallerHash = '';
        this.versionResolver = new VersionResolver_1.VersionResolver(context);
        this.fileUtilities = new FileUtilities_1.FileUtilities();
    }
    /**
     *
     * @returns The url to the installer for the sdk that matches the machine os and architecture, as well as for the requestedVersion.
     */
    getInstallerUrl() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.determineVersionAndInstallerUrl();
            return this.discoveredInstallerUrl;
        });
    }
    /**
     *
     * @returns The fully specified version in a standardized format that was requested.
     */
    getFullySpecifiedVersion() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.determineVersionAndInstallerUrl();
            return this.fullySpecifiedVersionRequested;
        });
    }
    /**
     *
     * @returns The url to the installer for the sdk that matches the machine os and architecture, as well as for the requestedVersion.
     */
    getInstallerHash() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.determineVersionAndInstallerUrl();
            return this.expectedInstallerHash;
        });
    }
    determineVersionAndInstallerUrl() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.fullySpecifiedVersionRequested === '' || this.discoveredInstallerUrl === '') {
                [this.discoveredInstallerUrl, this.fullySpecifiedVersionRequested, this.expectedInstallerHash] = yield this.routeRequestToProperVersionRequestType(this.requestedVersion);
            }
        });
    }
    /**
     *
     * @remarks this function maps the input version to a singular, specific and correct format based on the accepted version formats for global sdk installs.
     * @param version The requested version given to the API.
     * @returns The installer download URL for the correct OS, Architecture, & Specific Version based on the given input version, and then the resolved version we determined to install,
     * ... followed by the expected hash.
     */
    routeRequestToProperVersionRequestType(version) {
        return __awaiter(this, void 0, void 0, function* () {
            if (versionUtils.isNonSpecificMajorOrMajorMinorVersion(version)) {
                this.context.eventStream.post(new EventStreamEvents_1.DotnetVersionCategorizedEvent(`The VersionResolver resolved the version ${version} to be major, or major.minor.`));
                const numberOfPeriods = version.split('.').length - 1;
                const indexUrl = this.getIndexUrl(numberOfPeriods === 0 ? `${version}.0` : version);
                const indexJsonData = yield this.fetchJsonObjectFromUrl(indexUrl);
                const fullySpecifiedVersionRequested = indexJsonData[this.releasesLatestSdkKey];
                const installerUrlAndHash = yield this.findCorrectInstallerUrlAndHash(fullySpecifiedVersionRequested, indexUrl);
                return [installerUrlAndHash[0], fullySpecifiedVersionRequested, installerUrlAndHash[1]];
            }
            else if (versionUtils.isNonSpecificFeatureBandedVersion(version)) {
                this.context.eventStream.post(new EventStreamEvents_1.DotnetVersionCategorizedEvent(`The VersionResolver resolved the version ${version} to be a N.Y.XXX version.`));
                const fullySpecifiedVersion = yield this.getNewestSpecificVersionFromFeatureBand(version);
                const installerUrlAndHash = yield this.findCorrectInstallerUrlAndHash(fullySpecifiedVersion, this.getIndexUrl(versionUtils.getMajorMinor(fullySpecifiedVersion, this.context.eventStream, this.context)));
                return [installerUrlAndHash[0], fullySpecifiedVersion, installerUrlAndHash[1]];
            }
            else if (versionUtils.isFullySpecifiedVersion(version, this.context.eventStream, this.context)) {
                this.context.eventStream.post(new EventStreamEvents_1.DotnetVersionCategorizedEvent(`The VersionResolver resolved the version ${version} to be a fully specified version.`));
                const fullySpecifiedVersionRequested = version;
                const indexUrl = this.getIndexUrl(versionUtils.getMajorMinor(fullySpecifiedVersionRequested, this.context.eventStream, this.context));
                const installerUrlAndHash = yield this.findCorrectInstallerUrlAndHash(fullySpecifiedVersionRequested, indexUrl);
                return [installerUrlAndHash[0], fullySpecifiedVersionRequested, installerUrlAndHash[1]];
            }
            const err = new EventStreamEvents_1.DotnetVersionResolutionError(new EventStreamEvents_1.EventCancellationError('DotnetVersionResolutionError', `${this.badResolvedVersionErrorString} ${version}`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
            this.context.eventStream.post(err);
            throw err.error;
        });
    }
    /**
     *
     * @remarks this function handles finding the right os, arch url for the installer.
     * @param specificVersion the full, specific version, e.g. 7.0.301 to get.
     * @param indexUrl The url of the index server that hosts installer download links.
     * @returns The installer url to download as the first item of a tuple and then the expected hash of said installer
     */
    findCorrectInstallerUrlAndHash(specificVersion, indexUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (specificVersion === null || specificVersion === undefined || specificVersion === '') {
                const versionErr = new EventStreamEvents_1.DotnetVersionResolutionError(new EventStreamEvents_1.EventCancellationError('DotnetVersionResolutionError', `${this.badResolvedVersionErrorString} ${specificVersion}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                this.context.eventStream.post(versionErr);
                throw versionErr.error;
            }
            const convertedOs = this.fileUtilities.nodeOSToDotnetOS(os.platform(), this.context.eventStream);
            if (convertedOs === 'auto') {
                const osErr = new EventStreamEvents_1.DotnetUnexpectedInstallerOSError(new EventStreamEvents_1.EventBasedError('DotnetUnexpectedInstallerOSError', `The OS ${os.platform()} is currently unsupported or unknown.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                this.context.eventStream.post(osErr);
                throw osErr.error;
            }
            const convertedArch = this.fileUtilities.nodeArchToDotnetArch(os.arch(), this.context.eventStream);
            if (convertedArch === 'auto') {
                const archErr = new EventStreamEvents_1.DotnetUnexpectedInstallerArchitectureError(new EventStreamEvents_1.EventBasedError('DotnetUnexpectedInstallerArchitectureError', `The architecture ${os.arch()} is currently unsupported or unknown.
                Your architecture: ${os.arch()}. Your OS: ${os.platform()}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                this.context.eventStream.post(archErr);
                throw archErr.error;
            }
            const desiredRidPackage = `${convertedOs}-${convertedArch}`;
            const indexJson = yield this.fetchJsonObjectFromUrl(indexUrl);
            const releases = indexJson[this.releasesJsonKey];
            if (((_a = releases === null || releases === void 0 ? void 0 : releases.length) !== null && _a !== void 0 ? _a : 0) === 0) {
                const jsonErr = new EventStreamEvents_1.DotnetInvalidReleasesJSONError(new EventStreamEvents_1.EventBasedError('DotnetInvalidReleasesJSONError', `${this.releasesJsonErrorString}${indexUrl}`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                this.context.eventStream.post(jsonErr);
                throw jsonErr.error;
            }
            const sdks = [];
            const releasesKeyAlias = this.releasesSdksKey; // the forEach creates a separate 'this', so we introduce this copy to reduce ambiguity to the compiler
            releases.forEach(function (release) {
                // eslint-disable-next-line prefer-spread
                sdks.push.apply(sdks, release[releasesKeyAlias]);
            });
            for (const sdk of sdks) {
                const thisSDKVersion = sdk[this.releasesSdkVersionKey];
                if (thisSDKVersion === specificVersion) // NOTE that this will not catch things like -preview or build number suffixed versions.
                 {
                    const thisSDKFiles = sdk[this.releasesSdkFileKey];
                    for (const installer of thisSDKFiles) {
                        if (installer[this.releasesSdkRidKey] === desiredRidPackage && this.installerMatchesDesiredFileExtension(specificVersion, installer, convertedOs)) {
                            const installerUrl = installer[this.releasesUrlKey];
                            if (installerUrl === undefined) {
                                const releaseJsonErr = new EventStreamEvents_1.DotnetInvalidReleasesJSONError(new EventStreamEvents_1.EventBasedError('DotnetInvalidReleasesJSONError', `URL for ${desiredRidPackage} on ${specificVersion} is unavailable:
The version may be Out of Support, or the releases json format used by ${indexUrl} may be invalid and the extension needs to be updated.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                                this.context.eventStream.post(releaseJsonErr);
                                throw releaseJsonErr.error;
                            }
                            if (!this.startsWithAny(installerUrl, [
                                'https://download.visualstudio.microsoft.com/', 'https://builds.dotnet.microsoft.com/', 'https://ci.dot.net',
                                'https://dotnetcli.blob.core.windows.net/',
                            ])) {
                                const releaseJsonErr = new EventStreamEvents_1.DotnetInvalidReleasesJSONError(new EventStreamEvents_1.EventBasedError('DotnetInvalidReleasesJSONError', `The url: ${installerUrl} is hosted on an unexpected domain.
We cannot verify that .NET downloads are hosted in a secure location, so we have rejected .NET. The url should be download.visualstudio.microsoft.com.
Please report this issue so it can be remedied or investigated.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                                this.context.eventStream.post(releaseJsonErr);
                                throw releaseJsonErr.error;
                            }
                            else {
                                this.context.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityCheckEvent(`This installer file is hosted on an expected domain https://download.visualstudio.microsoft.com/.`));
                            }
                            let installerHash = installer[this.releasesHashKey];
                            if (!installerHash) {
                                installerHash = null;
                            }
                            return [installerUrl, installerHash];
                        }
                    }
                    const installerErr = new EventStreamEvents_1.DotnetNoInstallerFileExistsError(new EventStreamEvents_1.EventBasedError('DotnetNoInstallerFileExistsError', `An installer for the runtime ${desiredRidPackage} could not be found for version ${specificVersion}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                    this.context.eventStream.post(installerErr);
                    throw installerErr.error;
                }
            }
            const fileErr = new EventStreamEvents_1.DotnetNoInstallerFileExistsError(new EventStreamEvents_1.EventBasedError('DotnetNoInstallerFileExistsError', `The SDK installation files for version ${specificVersion} running on ${desiredRidPackage} couldn't be found.
Is the version in support? Note that -preview versions or versions with build numbers aren't yet supported.
Visit https://dotnet.microsoft.com/platform/support/policy/dotnet-core for support information.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
            this.context.eventStream.post(fileErr);
            throw fileErr.error;
        });
    }
    /**
     *
     * @param majorMinor the major.minor in the form of '3.1', etc.
     * @returns the url to obtain the installer for the version.
     */
    getIndexUrl(majorMinor) {
        return `https://builds.dotnet.microsoft.com/dotnet/release-metadata/${majorMinor}/releases.json`;
    }
    /**
     * @remarks The releases json may contain both zips and exes or others that match the RID.
     * We need to make sure we get the desired file type for each OS.
     *
     * @returns true if the filetype of the installer json entry containing the installer file name in the key 'name' is of a desired installer file extension type.
     * (e.g. EXE on windows or PKG on mac.)
     */
    installerMatchesDesiredFileExtension(version, installerJson, operatingSystemInDotnetFormat) {
        const installerFileName = installerJson[this.releasesSdkNameKey];
        if (installerFileName === undefined) {
            const err = new EventStreamEvents_1.DotnetInvalidReleasesJSONError(new EventStreamEvents_1.EventBasedError('DotnetInvalidReleasesJSONError', `${this.releasesJsonErrorString}
                ${this.getIndexUrl(versionUtils.getMajorMinor(version, this.context.eventStream, this.context))}.
The json does not have the parameter ${this.releasesSdkNameKey} which means the API publisher has published invalid dotnet release data.
Please file an issue at https://github.com/dotnet/vscode-dotnet-runtime.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
            this.context.eventStream.post(err);
            throw err.error;
        }
        let desiredFileExtension = '';
        switch (operatingSystemInDotnetFormat) {
            case 'win': {
                desiredFileExtension = '.exe';
                break;
            }
            case 'osx': {
                desiredFileExtension = '.pkg';
                break;
            }
            case 'linux': {
                desiredFileExtension = '.gz';
                break;
            }
            default:
                {
                    const err = new EventStreamEvents_1.DotnetUnexpectedInstallerOSError(new EventStreamEvents_1.EventBasedError('DotnetUnexpectedInstallerOSError', `The SDK Extension failed to map the OS ${operatingSystemInDotnetFormat} to a proper package type.
Your architecture: ${os.arch()}. Your OS: ${os.platform()}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                    this.context.eventStream.post(err);
                    throw err.error;
                }
        }
        return path.extname(installerFileName) === desiredFileExtension;
    }
    /**
     *
     * @param version the non-specific version, such as 6.0.4xx.
     * @param band The band of the version.
     */
    getNewestSpecificVersionFromFeatureBand(version) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const band = versionUtils.getFeatureBandFromVersion(version, this.context.eventStream, this.context);
            const indexUrl = this.getIndexUrl(versionUtils.getMajorMinor(version, this.context.eventStream, this.context));
            // Get the sdks
            const indexJson = yield this.fetchJsonObjectFromUrl(indexUrl);
            const releases = indexJson[this.releasesJsonKey];
            if (((_a = releases === null || releases === void 0 ? void 0 : releases.length) !== null && _a !== void 0 ? _a : 0) === 0) {
                const badJsonErr = new EventStreamEvents_1.DotnetInvalidReleasesJSONError(new EventStreamEvents_1.EventBasedError('DotnetInvalidReleasesJSONError', `${this.releasesJsonErrorString}${indexUrl}`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                this.context.eventStream.post(badJsonErr);
                throw badJsonErr.error;
            }
            // Assumption: The first release in releases will be the newest release and contain the newest sdk for each feature band. This has been 'confirmed' with the releases team.
            const sdks = releases[0][this.releasesSdksKey];
            for (const sdk of sdks) {
                // The SDKs in the index should be in-order, so we can rely on that property.
                // The first one we find with the given feature band will also be the 'newest.'
                const thisSDKVersion = sdk[this.releasesSdkVersionKey];
                if (versionUtils.getFeatureBandFromVersion(thisSDKVersion, this.context.eventStream, this.context) === band) {
                    return thisSDKVersion;
                }
            }
            const availableBands = Array.from(new Set(sdks.map((x) => versionUtils.getFeatureBandFromVersion(x[this.releasesSdkVersionKey], this.context.eventStream, this.context))));
            const err = new EventStreamEvents_1.DotnetFeatureBandDoesNotExistError(new EventStreamEvents_1.EventBasedError('DotnetFeatureBandDoesNotExistError', `The feature band '${band}' doesn't exist for the SDK major version '${version}'.
Available feature bands for this SDK version are ${availableBands}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
            this.context.eventStream.post(err);
            throw err.error;
        });
    }
    startsWithAny(str, substrings) {
        for (const substring of substrings) {
            if (str.startsWith(substring)) {
                return true;
            }
        }
        return false;
    }
    /**
     *
     * @param url The url containing raw json data to parse.
     * @returns a serialized JSON object.
     * @remarks A wrapper around the real web request worker class to call into either the mock or real web worker. The main point of this function is  to dedupe logic.
     */
    fetchJsonObjectFromUrl(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const webWorker = this.customWebRequestWorker ? this.customWebRequestWorker : WebRequestWorkerSingleton_1.WebRequestWorkerSingleton.getInstance();
            return webWorker.getCachedData(url, this.context);
        });
    }
}
exports.GlobalInstallerResolver = GlobalInstallerResolver;
//# sourceMappingURL=GlobalInstallerResolver.js.map